'use client'
import {
  context_default
} from "./chunk-ZF2ENNTR.mjs";
import {
  COOKIE_REFRESH_TOKEN,
  COOKIE_TOKEN,
  COOKIE_TOKEN_EXPIRES_IN
} from "./chunk-5KF4VIOG.mjs";
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/context/provider.tsx
import { useEffect, useMemo, useState } from "react";
import { ApiConfig, refreshTokens } from "@wbk/api";
import { useGetUser } from "@wbk/api/hooks";
import { createCookie, eraseCookie, loadScriptAsync, readCookie } from "@wbk/utils";
import { useAnalytics } from "@wbk/analytics";
import { useRouter } from "@wbk/config";
import { jsx } from "react/jsx-runtime";
var tokenRefreshTimeoutId;
var AuthProvider = (_a) => {
  var _b = _a, { children, refreshToken = true } = _b, props = __objRest(_b, ["children", "refreshToken"]);
  var _a2, _b2, _c;
  const [state, setState] = useState(__spreadProps(__spreadValues({}, props), {
    refreshToken,
    canFetchUser: !refreshToken
  }));
  const { loginEvent } = useAnalytics();
  const { locale } = useRouter();
  const {
    data: user,
    isPending,
    isFetching
  } = useGetUser({ lang: locale }, { enabled: state.autoInit && state.canFetchUser });
  const fetchUser = () => {
    if (state.autoInit)
      return;
    setState((prev) => __spreadProps(__spreadValues({}, prev), { autoInit: true }));
  };
  const setTokens = ({
    accessToken,
    refreshToken: refreshToken2,
    tokenExpiresIn
  }) => {
    var _a3, _b3, _c2, _d, _e, _f, _g, _h, _i, _j, _k;
    const tokenExpiryTime = tokenExpiresIn ? Date.now() + tokenExpiresIn * 1e3 : void 0;
    const expiresIn = tokenExpiresIn ? tokenExpiresIn / (60 * 60 * 24) : (_b3 = (_a3 = ApiConfig.config) == null ? void 0 : _a3.cookie) == null ? void 0 : _b3.tokenExpiration;
    if (accessToken) {
      createCookie({
        name: COOKIE_TOKEN,
        value: accessToken,
        expiration: expiresIn,
        domain: (_e = (_d = (_c2 = ApiConfig) == null ? void 0 : _c2.config) == null ? void 0 : _d.cookie) == null ? void 0 : _e.domain
      });
    }
    if (refreshToken2) {
      createCookie({
        name: COOKIE_REFRESH_TOKEN,
        value: refreshToken2,
        expiration: Number.MAX_SAFE_INTEGER,
        domain: (_h = (_g = (_f = ApiConfig) == null ? void 0 : _f.config) == null ? void 0 : _g.cookie) == null ? void 0 : _h.domain
      });
    }
    if (tokenExpiryTime) {
      createCookie({
        name: COOKIE_TOKEN_EXPIRES_IN,
        value: tokenExpiryTime.toString(),
        expiration: Number.MAX_SAFE_INTEGER,
        domain: (_k = (_j = (_i = ApiConfig) == null ? void 0 : _i.config) == null ? void 0 : _j.cookie) == null ? void 0 : _k.domain
      });
    }
    if (refreshToken2 && tokenExpiresIn) {
      startRefreshTokenTimer(refreshToken2, tokenExpiresIn);
    }
  };
  const updateTokens = (refreshToken2) => __async(void 0, null, function* () {
    var _a3, _b3;
    try {
      const url = `https://www.google.com/recaptcha/api.js?render=${(_b3 = (_a3 = ApiConfig.config) == null ? void 0 : _a3.grecaptcha) == null ? void 0 : _b3.v3Key}`;
      yield loadScriptAsync({ id: "recaptcha_script", url });
      const { data } = yield refreshTokens(refreshToken2);
      setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        tokenExpiresIn: data.token_expires_in
      });
    } finally {
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
    }
  });
  const startRefreshTokenTimer = (refreshToken2, tokenExpiresIn) => {
    if (!state.refreshToken)
      return;
    if (tokenRefreshTimeoutId) {
      clearTimeout(tokenRefreshTimeoutId);
    }
    const timeRemaining = tokenExpiresIn * 1e3;
    tokenRefreshTimeoutId = setTimeout(() => {
      updateTokens(refreshToken2);
    }, timeRemaining);
  };
  const clearTokens = () => {
    var _a3, _b3, _c2, _d, _e, _f, _g, _h, _i;
    if (tokenRefreshTimeoutId) {
      clearTimeout(tokenRefreshTimeoutId);
    }
    eraseCookie(COOKIE_TOKEN, (_c2 = (_b3 = (_a3 = ApiConfig) == null ? void 0 : _a3.config) == null ? void 0 : _b3.cookie) == null ? void 0 : _c2.domain);
    eraseCookie(COOKIE_REFRESH_TOKEN, (_f = (_e = (_d = ApiConfig) == null ? void 0 : _d.config) == null ? void 0 : _e.cookie) == null ? void 0 : _f.domain);
    eraseCookie(COOKIE_TOKEN_EXPIRES_IN, (_i = (_h = (_g = ApiConfig) == null ? void 0 : _g.config) == null ? void 0 : _h.cookie) == null ? void 0 : _i.domain);
  };
  useEffect(() => {
    const storageRefreshToken = readCookie(COOKIE_REFRESH_TOKEN);
    const storageTokenExpiresIn = readCookie(COOKIE_TOKEN_EXPIRES_IN);
    if (!storageRefreshToken || !storageTokenExpiresIn) {
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
      return;
    }
    const expiryTime = parseInt(storageTokenExpiresIn, 10);
    const timeRemaining = expiryTime - Date.now();
    if (timeRemaining > 0) {
      startRefreshTokenTimer(storageRefreshToken, timeRemaining / 1e3);
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
    } else {
      updateTokens(storageRefreshToken);
    }
  }, []);
  const isNewUser = useMemo(() => {
    return !!(user == null ? void 0 : user._id) && (!!(user == null ? void 0 : user.is_guest) || !!(user == null ? void 0 : user.force_verify_email));
  }, [user == null ? void 0 : user._id, user == null ? void 0 : user.force_verify_email, user == null ? void 0 : user.is_guest]);
  const isTawakkalnaUser = useMemo(() => {
    return readCookie("twk") === "true";
  }, []);
  useEffect(() => {
    if (user) {
      loginEvent({
        method: "email",
        user: __spreadProps(__spreadValues({}, user), { locale })
      });
    }
  }, [user]);
  return /* @__PURE__ */ jsx(
    context_default.Provider,
    {
      value: __spreadProps(__spreadValues({}, state), {
        newsLetter: {
          enable: (_b2 = (_a2 = props.newsLetter) == null ? void 0 : _a2.enable) != null ? _b2 : true,
          organizationName: ((_c = props.newsLetter) == null ? void 0 : _c.organizationName) || "webook.com"
        },
        user,
        isNewUser,
        isTawakkalnaUser,
        loading: isPending,
        isFetching,
        fetchUser,
        setTokens,
        clearTokens
      }),
      children
    }
  );
};

export {
  AuthProvider
};
//# sourceMappingURL=chunk-LARJJETO.mjs.map